import React, { useMemo } from 'react';
import { notifyError } from '../lib/notify';
import { useTranslation } from 'react-i18next';
import { PageTitle, Meta, Display, Chip, Icons } from '../components/Atoms';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useMultipleApi, daysSince, taskTypeMeta } from '../lib/api';
import { formatLocalDate } from '../i18n';

/**
 * Reminders - przypomnienia z prawdziwych danych konta zalogowanego.
 *
 * 4 sekcje:
 * 1. KARENCJE (sticky warning) - z /api/reminders (auto-generowane po opryskach)
 * 2. DZIŚ - taski z due_date == dziś
 * 3. NAJBLIŻSZE 7 dni - taski w przedziale jutro..+7
 * 4. CYKLICZNE - taski z is_recurring=1
 *
 * Endpoints:
 *   /api/reminders         - karencja warnings (auto z care_history)
 *   /api/tasks?limit=200   - wszystkie taski
 */

const TONE_COLOR = {
  sage:  'var(--t-sage)',
  rust:  'var(--t-rust)',
  amber: 'var(--t-amber)',
  dim:   'var(--t-text-mute)',
};

const ICONS = {
  sprout: Icons.Sprout,
  scissors: Icons.Scissors,
  drop: Icons.Drop,
  sun: Icons.Sun,
};

// ── Helpers ──────────────────────────────────────────────────────
function isoDate(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function categorizeTasks(tasks) {
  const today = isoDate(new Date());
  const out = { today: [], upcoming: [], recurring: [] };
  tasks.forEach(t => {
    if (t.completed) return;
    if (t.is_recurring) {
      out.recurring.push(t);
      return;
    }
    if (!t.due_date) return;
    const d = t.due_date.slice(0, 10);
    if (d === today) out.today.push(t);
    else if (d > today) out.upcoming.push(t);
  });
  // sortuj po dacie
  out.upcoming.sort((a, b) => a.due_date.localeCompare(b.due_date));
  return out;
}

// ── Karencja warning card ────────────────────────────────────────
function WithdrawalWarning({ reminder, onMarkRead }) {
  const safeIn = reminder.safe_harvest_date ? -daysSince(reminder.safe_harvest_date) : null;
  const [busy, setBusy] = React.useState(false);

  const handleMark = async (e) => {
    e?.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await onMarkRead?.(reminder.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      padding: '16px 20px', borderRadius: 14,
      background: 'rgba(192, 106, 74, 0.08)',
      borderLeft: '3px solid var(--t-rust)',
      marginBottom: 8,
      display: 'flex', gap: 14, alignItems: 'flex-start',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Meta color="rust" style={{ marginBottom: 6 }}>
          ⚠ Karencja · {reminder.spray_name}
        </Meta>
        <Display size={16} style={{ display: 'block', marginBottom: 4 }}>
          {reminder.message}
        </Display>
        <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginBottom: 8 }}>
          {[reminder.plot_name, reminder.plant_name && `${reminder.plant_name} · rząd ${reminder.row_number}`]
            .filter(Boolean).join(' · ')}
        </div>
        {safeIn != null && (
          <Meta color="rust">
            {safeIn > 0 ? `Zostało ${safeIn} dni` : 'Karencja minęła — można zbierać'}
          </Meta>
        )}
      </div>
      <button
        type="button"
        onClick={handleMark}
        disabled={busy}
        aria-label="Oznacz jako przeczytane"
        title="Oznacz jako przeczytane"
        style={{
          background: 'transparent',
          border: '1px solid var(--t-rust)',
          color: 'var(--t-rust)',
          padding: '6px 10px', borderRadius: 6,
          cursor: busy ? 'wait' : 'pointer',
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.18em', textTransform: 'uppercase',
          fontWeight: 500, flexShrink: 0, marginTop: 2,
          transition: 'all 200ms ease',
        }}
        onMouseEnter={e => {
          if (busy) return;
          e.currentTarget.style.background = 'var(--t-rust)';
          e.currentTarget.style.color = 'var(--t-bg)';
        }}
        onMouseLeave={e => {
          if (busy) return;
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--t-rust)';
        }}
      >{busy ? '…' : '✓ OK'}</button>
    </div>
  );
}

// ── Reminder row (task) ──────────────────────────────────────────
function ReminderRow({ task, showDate }) {
  const meta = taskTypeMeta(task.task_type);
  const Icon = ICONS[meta.iconName] || Icons.Sun;
  const color = TONE_COLOR[meta.tone] || TONE_COLOR.sage;

  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'center',
      padding: '14px 0',
      borderBottom: '1px solid var(--t-line)',
      cursor: 'pointer',
    }}>
      <div style={{
        width: 4, height: 36, borderRadius: 2,
        background: color, flexShrink: 0,
      }} />
      <div style={{
        width: 36, height: 36, borderRadius: 9,
        background: 'var(--t-dim-bg)',
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <Icon color={color} size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Display size={16} style={{ display: 'block', lineHeight: 1.2 }}>
          {task.description}
        </Display>
        {(task.plant_name || task.plot_name) && (
          <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 3 }}>
            {[task.plot_name, task.plant_name].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
      <Meta color={task.priority >= 3 ? 'rust' : 'dim'}>
        {showDate ? formatLocalDate(task.due_date, 'short') : (task.time_of_day || '')}
      </Meta>
    </div>
  );
}

// ── Recurring row ────────────────────────────────────────────────
function RecurringRow({ task }) {
  const meta = taskTypeMeta(task.task_type);
  const Icon = ICONS[meta.iconName] || Icons.Sun;
  const color = TONE_COLOR[meta.tone] || TONE_COLOR.sage;
  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'center',
      padding: '14px 0',
      borderBottom: '1px solid var(--t-line)',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 9,
        background: 'rgba(168, 196, 158, 0.12)',
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 8a6 6 0 0110.5-4M14 8a6 6 0 01-10.5 4M11 4h2.5V1.5M5 12H2.5V14.5"
                stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Display size={16} style={{ display: 'block', lineHeight: 1.2 }}>
          {task.description}
        </Display>
        <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 3 }}>
          {[task.plant_name, `co ${task.recurrence_frequency || '?'} dni`].filter(Boolean).join(' · ')}
        </div>
      </div>
      {task.next_occurrence && (
        <Meta color="sage">{formatLocalDate(task.next_occurrence, 'short')}</Meta>
      )}
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────────
function Section({ title, count, items, render, color = 'dim', emptyText }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 4, paddingBottom: 8,
        borderBottom: '1px solid var(--t-line-2)',
      }}>
        <Meta color={color}>
          {title}{count != null && ` · ${count}`}
        </Meta>
      </div>
      {items.length === 0 ? (
        <div style={{
          padding: '16px 0', fontSize: 13,
          color: 'var(--t-text-mute)', fontStyle: 'italic',
        }}>↳ {emptyText}</div>
      ) : items.map(render)}
    </div>
  );
}

// ── Main content ────────────────────────────────────────────────
function MainContent({ tasks, withdrawals, onMarkRead, onMarkAllRead, markAllBusy }) {
  const { t } = useTranslation();
  const cats = useMemo(() => categorizeTasks(tasks), [tasks]);

  return (
    <div className="t-stagger">
      {/* Karencja warnings - sticky na górze */}
      {withdrawals.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {withdrawals.length > 1 && (
            <div style={{
              display: 'flex', justifyContent: 'flex-end', marginBottom: 8,
            }}>
              <button
                type="button"
                onClick={onMarkAllRead}
                disabled={markAllBusy}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'var(--t-rust)', cursor: markAllBusy ? 'wait' : 'pointer',
                  fontFamily: 'var(--t-mono)', fontSize: 11,
                  letterSpacing: '.16em', textTransform: 'uppercase',
                  padding: '4px 8px',
                }}
              >{markAllBusy ? 'Czyszczę…' : `✓ Oznacz wszystkie (${withdrawals.length})`}</button>
            </div>
          )}
          {withdrawals.map(r => (
            <WithdrawalWarning key={r.id} reminder={r} onMarkRead={onMarkRead} />
          ))}
        </div>
      )}

      <Section
        title={`${t('common.today')} · ${formatLocalDate(new Date(), 'short')}`}
        count={cats.today.length}
        items={cats.today}
        color="sage"
        render={r => <ReminderRow key={r.id} task={r} />}
        emptyText="Brak zadań na dziś"
      />

      <Section
        title="Najbliższe"
        count={cats.upcoming.length}
        items={cats.upcoming.slice(0, 12)}
        render={r => <ReminderRow key={r.id} task={r} showDate />}
        emptyText="Brak zaplanowanych zadań"
      />

      <Section
        title="Cykliczne"
        count={cats.recurring.length}
        items={cats.recurring}
        render={r => <RecurringRow key={r.id} task={r} />}
        emptyText="Brak cyklicznych zadań — sukcesyjny wysiew warto skonfigurować"
      />
    </div>
  );
}

// ── Stats sidebar ───────────────────────────────────────────────
function StatsSidebar({ tasks, withdrawals }) {
  const cats = useMemo(() => categorizeTasks(tasks), [tasks]);
  const completed = tasks.filter(t => t.completed).length;
  return (
    <div>
      <Meta style={{ marginBottom: 6 }}>Co czeka</Meta>
      <Display size={48} style={{ display: 'block', letterSpacing: '-.03em' }}>Przypomnienia</Display>

      <div style={{ marginTop: 24, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Chip selected>Wszystkie</Chip>
        {withdrawals.length > 0 && <Chip tone="rust">Karencja · {withdrawals.length}</Chip>}
        <Chip tone="sage">Cykliczne · {cats.recurring.length}</Chip>
      </div>

      <div style={{
        marginTop: 32, padding: '20px 22px',
        borderRadius: 14, background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
      }}>
        <Meta style={{ marginBottom: 12 }}>Sezon w liczbach</Meta>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Row label="Wykonane" value={completed} color="var(--t-sage)" />
          <Row label="Najbliższe 7 dni" value={cats.upcoming.slice(0, 7).length} />
          <Row label="Cykliczne" value={cats.recurring.length} />
          {withdrawals.length > 0 && (
            <Row label="Karencja" value={withdrawals.length} color="var(--t-rust)" />
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: 13, color: color || 'var(--t-text-dim)' }}>{label}</span>
      <Display size={24} style={{ color: color || 'var(--t-text)' }}>{value}</Display>
    </div>
  );
}

// ── Mobile content ──────────────────────────────────────────────
function MobileContent({ tasks, withdrawals, ...rest }) {
  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 24px 0',
      }}>
        <Meta>Wszystkie sezon · {tasks.filter(t => !t.completed).length}</Meta>
      </div>
      <PageTitle title="Przypomnienia" eyebrow="Co czeka" />
      <div style={{ padding: '0 24px' }}>
        <MainContent tasks={tasks} withdrawals={withdrawals} {...rest} />
      </div>
    </div>
  );
}

// ── Desktop content ─────────────────────────────────────────────
function DesktopContent({ tasks, withdrawals, ...rest }) {
  return (
    <div style={{
      padding: 'clamp(20px, 3vw, 32px) clamp(20px, 3vw, 40px) 60px',
      display: 'grid',
      gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)',
      gap: 'clamp(20px, 3vw, 48px)', alignItems: 'start',
    }}>
      <StatsSidebar tasks={tasks} withdrawals={withdrawals} />
      <MainContent tasks={tasks} withdrawals={withdrawals} {...rest} />
    </div>
  );
}

// ── Reminders screen root ────────────────────────────────────────
export default function Reminders() {
  const { t } = useTranslation();
  const { isPhone, isTablet } = useViewport();
  const [markAllBusy, setMarkAllBusy] = React.useState(false);

  const { results, loading, errors, refetchAll } = useMultipleApi({
    tasks:     '/api/tasks?limit=200',
    reminders: '/api/reminders',
  });

  const handleMarkRead = async (id) => {
    try {
      const axios = (await import('../../config/axios')).default;
      await axios.put(`/api/reminders/${id}/read`);
      refetchAll?.();
    } catch (err) {
      notifyError('Nie udało się oznaczyć: ' + (err.response?.data?.error || ''));
    }
  };

  const handleMarkAllRead = async () => {
    if (markAllBusy) return;
    setMarkAllBusy(true);
    try {
      const axios = (await import('../../config/axios')).default;
      await axios.put('/api/reminders/read-all');
      refetchAll?.();
    } catch (err) {
      notifyError('Nie udało się oznaczyć wszystkich: ' + (err.response?.data?.error || ''));
    } finally {
      setMarkAllBusy(false);
    }
  };

  const tasksError = errors.tasks;
  if (tasksError && (tasksError.includes('autoryz') || tasksError === 'Brak tokenu autoryzacji')) {
    return <Layout active="cal"><ErrorState error={{ status: 401, message: tasksError }} /></Layout>;
  }
  if (loading) return <Layout active="cal"><LoadingState label={t('common.loading')} /></Layout>;

  const tasks = results.tasks?.data || [];
  const withdrawals = (results.reminders || []).filter(r => !r.is_read);

  const sharedProps = {
    tasks, withdrawals,
    onMarkRead: handleMarkRead,
    onMarkAllRead: handleMarkAllRead,
    markAllBusy,
  };

  if (isPhone || isTablet) return <Layout active="cal"><MobileContent {...sharedProps} /></Layout>;

  return (
    <Layout active="cal" topBar={<TopBar
      left={<Meta>Kalendarz · Przypomnienia</Meta>}
    />}>
      <DesktopContent {...sharedProps} />
    </Layout>
  );
}
